import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

interface GDPRPolicyProps {
  onNavigate?: (route: string) => void;
}

export function GDPRPolicy({ onNavigate }: GDPRPolicyProps) {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-card p-8 rounded-lg shadow border">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            style={{ paddingLeft: "0" }}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="text-sm text-muted-foreground mb-1"><strong>Source Code Control Limited</strong></p>
          <p className="text-sm text-muted-foreground mb-4"><em>ORbiT8 - Cloud Software Supply Chain Scanning</em></p>
          <h1 className="text-3xl font-bold text-foreground mb-2">GDPR Policy</h1>
          <h2 className="text-xl font-semibold text-foreground mb-4">Data Protection and GDPR Compliance Statement</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Effective Date: April 2026 | Version 1.0
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-border">
              <tbody>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Company</td>
                  <td className="px-4 py-2">Source Code Control Limited</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Product</td>
                  <td className="px-4 py-2">ORbiT8</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Website</td>
                  <td className="px-4 py-2">
                    <a href="https://www.orbit8.co" className="text-primary hover:underline">orbit8.co</a>; <a href="https://sourcecodecontrol.com" className="text-primary hover:underline">sourcecodecontrol.com</a>
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="px-4 py-2 font-bold bg-muted">Contact</td>
                  <td className="px-4 py-2">
                    <a href="mailto:open@sourcecodecontrol.com" className="text-primary hover:underline">open@sourcecodecontrol.com</a>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-bold bg-muted">Registered Office</td>
                  <td className="px-4 py-2">153 Mortimer Street, Herne Bay, Kent, CT6 5HA, United Kingdom</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-muted border border-border rounded p-4 mb-8">
            <p className="text-sm italic">
              <strong>IMPORTANT:</strong> Please read this document carefully before using ORbiT8. By accessing or using the platform, you agree to be bound by these terms.
            </p>
          </div>

          <div className="space-y-6 text-foreground/80">

            <hr className="my-8 border-border" />

            <section id="introduction">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction and Scope</h2>
              <p>
                Source Code Control Limited (“the Company") is committed to processing personal data responsibly and in full compliance with applicable data protection legislation, including the UK General Data Protection Regulation (UK GDPR), the Data Protection Act 2018, and, where applicable, the EU General Data Protection Regulation (EU GDPR 2016/679).
              </p>
              <p className="mt-3">
                This GDPR Policy applies to all personal data processed by the Company in connection with the ORbiT8 platform and sets out the principles, roles, and responsibilities governing that processing. It should be read alongside our Privacy Policy and Terms and Conditions.
              </p>
              <p className="mt-3">
                This Policy applies to all employees, contractors, and third-party service providers who process personal data on behalf of Source Code Control Limited.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="principles">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Data Protection Principles</h2>
              <p>The Company processes personal data in accordance with the six principles set out in Article 5 of UK GDPR:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li><strong>Lawfulness, fairness, and transparency</strong> - Personal data is processed lawfully, fairly, and in a transparent manner.</li>
                <li><strong>Purpose limitation</strong> - Personal data is collected for specified, explicit, and legitimate purposes and not further processed in a manner incompatible with those purposes.</li>
                <li><strong>Data minimisation</strong> - Only personal data that is adequate, relevant, and limited to what is necessary is collected.</li>
                <li><strong>Accuracy</strong> - Personal data is kept accurate and, where necessary, kept up to date.</li>
                <li><strong>Storage limitation</strong> - Personal data is not kept in identifiable form for longer than necessary for the purposes for which it is processed.</li>
                <li><strong>Integrity and confidentiality</strong> - Personal data is processed with appropriate security, including protection against unauthorised or unlawful processing and against accidental loss, destruction, or damage.</li>
              </ul>
              <p className="mt-3">The Company is accountable for demonstrating compliance with these principles.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="lawful-basis">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. Lawful Basis for Processing</h2>
              <p>The Company relies on the following lawful bases under Article 6 UK GDPR for processing personal data in connection with ORbiT8:</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.1 Contract Performance (Article 6(1)(b))</h3>
              <p>
                Processing is necessary for the performance of a contract with the User, including creating and managing Accounts, delivering Scan Results, and providing support.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.2 Legitimate Interests (Article 6(1)(f))</h3>
              <p>
                Processing is necessary for our legitimate interests, including improving the Platform, ensuring security, preventing fraud, and communicating relevant service updates - provided these interests are not overridden by the interests or rights of the data subject.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.3 Legal Obligation (Article 6(1)(c))</h3>
              <p>
                Processing is necessary to comply with legal or regulatory obligations, including tax, accounting, and law enforcement requirements.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">3.4 Consent (Article 6(1)(a))</h3>
              <p>
                Where processing is based on consent (for example, marketing communications or non-essential analytics), the Company will obtain clear, specific, and freely given consent and maintain records of that consent. Consent may be withdrawn at any time.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="controller-processor">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Data Controller and Data Processor Responsibilities</h2>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">4.1 Source Code Control Limited as Data Controller</h3>
              <p>
                The Company acts as the data controller in respect of personal data collected directly from Users (such as Account registration data, usage data, and communications). As data controller, the Company determines the purposes and means of processing and is responsible for ensuring compliance with UK GDPR.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">4.2 Source Code Control Limited as Data Processor</h3>
              <p>
                Where Users submit Content to the Platform that contains personal data, the User acts as the data controller and Source Code Control Limited acts as a data processor. In this capacity, the Company:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>Processes personal data only on documented instructions from the User.</li>
                <li>Ensures that persons authorised to process the data are bound by appropriate confidentiality obligations.</li>
                <li>Implements appropriate technical and organisational security measures.</li>
                <li>Assists the data controller in meeting their obligations regarding data subject rights.</li>
                <li>Deletes or returns all personal data at the end of the service, as requested.</li>
                <li>Provides all information necessary to demonstrate compliance.</li>
              </ul>
              <p className="mt-3">
                Where processing of personal data within User Content is likely, Users are advised to implement a Data Processing Agreement (DPA) with the Company. Please contact <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a> to request a DPA.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="rights">
              <h2 className="text-2xl font-semibold text-foreground mb-4">5. Data Subject Rights</h2>
              <p>The Company respects and upholds the rights of data subjects under UK GDPR. The following rights apply:</p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.1 Right of Access (Article 15)</h3>
              <p>
                Data subjects may request confirmation of whether their personal data is being processed and, if so, access to that data and supplementary information about the processing.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.2 Right to Rectification (Article 16)</h3>
              <p>
                Data subjects may request correction of inaccurate personal data and completion of incomplete data.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.3 Right to Erasure (Article 17)</h3>
              <p>
                Data subjects may request deletion of their personal data where it is no longer necessary for the purpose collected, consent has been withdrawn, or processing is otherwise unlawful, subject to legal retention obligations.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.4 Right to Restriction of Processing (Article 18)</h3>
              <p>
                Data subjects may request restriction of processing in certain circumstances, for example while accuracy of data is contested.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.5 Right to Data Portability (Article 20)</h3>
              <p>
                Where processing is based on consent or contract and carried out by automated means, data subjects may request their data in a structured, commonly used, machine-readable format.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.6 Right to Object (Article 21)</h3>
              <p>
                Data subjects may object to processing based on legitimate interests or for direct marketing purposes. Where an objection to direct marketing is received, processing for that purpose must cease immediately.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.7 Rights Related to Automated Decision-Making (Article 22)</h3>
              <p>
                Data subjects have the right not to be subject to a decision based solely on automated processing that produces a significant effect on them. ORbiT8 does not currently make automated decisions that produce legal or similarly significant effects on individuals.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">5.8 Responding to Requests</h3>
              <p>
                The Company will respond to data subject rights requests within one calendar month of receipt. Requests should be submitted to <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>. The Company may ask for identity verification before fulfilling a request. No fee is charged for requests, unless they are manifestly unfounded or excessive.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="retention">
              <h2 className="text-2xl font-semibold text-foreground mb-4">6. Data Retention</h2>
              <p>
                Personal data is retained only for as long as necessary for the purposes for which it was collected, or as required by applicable law. The Company maintains a data retention schedule, which is reviewed annually. Key retention periods for ORbiT8 are:
              </p>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="px-4 py-2 text-left font-semibold border-b border-border">Data Category</th>
                      <th className="px-4 py-2 text-left font-semibold border-b border-border">Retention Period</th>
                      <th className="px-4 py-2 text-left font-semibold border-b border-border">Legal Basis for Retention</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2">Account &amp; registration data</td>
                      <td className="px-4 py-2">Duration of Account + 12 months</td>
                      <td className="px-4 py-2">Contract / Legitimate interests</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2">Usage and log data</td>
                      <td className="px-4 py-2">Up to 12 months</td>
                      <td className="px-4 py-2">Legitimate interests</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2">Content submitted for scanning</td>
                      <td className="px-4 py-2">Duration of active use; deleted on request or within 30 days of closure</td>
                      <td className="px-4 py-2">Contract</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2">Scan Results</td>
                      <td className="px-4 py-2">Duration of active use or as requested</td>
                      <td className="px-4 py-2">Contract</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2">Support communications</td>
                      <td className="px-4 py-2">Up to 3 years</td>
                      <td className="px-4 py-2">Legitimate interests</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2">Financial and legal records</td>
                      <td className="px-4 py-2">Up to 7 years</td>
                      <td className="px-4 py-2">Legal obligation</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">Consent records</td>
                      <td className="px-4 py-2">Duration of consent + 3 years</td>
                      <td className="px-4 py-2">Legal obligation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <hr className="my-8 border-border" />

            <section id="security">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Data Security</h2>
              <p>
                The Company implements appropriate technical and organisational measures to ensure a level of security appropriate to the risk, including:
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">7.1 Technical Measures</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Encryption of personal data in transit (TLS/HTTPS) and at rest.</li>
                <li>Access controls ensuring that only authorised personnel can access personal data.</li>
                <li>Regular security assessments and vulnerability testing of the Platform.</li>
                <li>Secure software development practices aligned with industry standards.</li>
                <li>Monitoring and logging of access to systems containing personal data.</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-4 mb-2">7.2 Organisational Measures</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Data protection training and awareness for all staff with access to personal data.</li>
                <li>Internal data protection policies and acceptable use procedures.</li>
                <li>Confidentiality obligations for all employees and contractors.</li>
                <li>Vendor due diligence to ensure third-party processors maintain equivalent standards.</li>
                <li>Regular review of data protection measures in line with evolving risks.</li>
              </ul>
            </section>

            <hr className="my-8 border-border" />

            <section id="breaches">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Personal Data Breaches</h2>
              <p>The Company has procedures in place for detecting, reporting, and investigating personal data breaches. In the event of a breach:</p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>The breach will be assessed to determine whether it is likely to result in risk to the rights and freedoms of individuals.</li>
                <li>Where required, the breach will be reported to the Information Commissioner’s Office within 72 hours of becoming aware of it.</li>
                <li>Affected individuals will be notified without undue delay where the breach is likely to result in high risk to their rights and freedoms.</li>
                <li>A record of all breaches will be maintained, including those not reported to the ICO.</li>
              </ul>
              <p className="mt-3">
                To report a suspected data breach, please contact us immediately at <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="transfers">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. International Data Transfers</h2>
              <p>
                The Company primarily processes personal data within the United Kingdom and the European Economic Area. Where data is transferred to countries outside these areas, the Company ensures appropriate safeguards are in place, including:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>Standard Contractual Clauses (SCCs) approved by the UK ICO or European Commission.</li>
                <li>Binding Corporate Rules where applicable.</li>
                <li>Adequacy decisions by the UK Government or European Commission.</li>
              </ul>
              <p className="mt-3">A record of all international transfers is maintained and reviewed to ensure ongoing adequacy of safeguards.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="processors">
              <h2 className="text-2xl font-semibold text-foreground mb-4">10. Third-Party Processors</h2>
              <p>
                The Company uses third-party service providers to support the operation of ORbiT8. All third-party processors are subject to due diligence, and appropriate Data Processing Agreements are in place. Third-party processors are permitted to process personal data only on documented instructions and may not use it for their own purposes.
              </p>
              <p className="mt-3">
                A record of all third-party processors is maintained and reviewed regularly. Processors are required to notify the Company of any changes that may affect their compliance with data protection obligations.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="dpias">
              <h2 className="text-2xl font-semibold text-foreground mb-4">11. Data Protection Impact Assessments (DPIAs)</h2>
              <p>
                The Company conducts Data Protection Impact Assessments (DPIAs) for processing activities that are likely to result in high risk to the rights and freedoms of individuals, in accordance with Article 35 UK GDPR. This includes:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-3">
                <li>Large-scale processing of special category data.</li>
                <li>Systematic monitoring of users.</li>
                <li>Processing involving new technologies.</li>
              </ul>
              <p className="mt-3">DPIAs are documented and reviewed as part of the Company’s data governance process.</p>
            </section>

            <hr className="my-8 border-border" />

            <section id="ropa">
              <h2 className="text-2xl font-semibold text-foreground mb-4">12. Records of Processing Activities</h2>
              <p>
                The Company maintains a Record of Processing Activities (ROPA) in accordance with Article 30 UK GDPR. This record documents all processing activities carried out by the Company as both data controller and data processor, including the purposes of processing, categories of data, data subjects, recipients, transfers, retention periods, and security measures.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="roles">
              <h2 className="text-2xl font-semibold text-foreground mb-4">13. Roles and Responsibilities</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>The Directors of Source Code Control Limited have overall accountability for data protection compliance.</li>
                <li>All employees and contractors processing personal data are responsible for compliance with this Policy and relevant data protection training.</li>
                <li>For queries regarding data protection, please contact <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>.</li>
              </ul>
              <p className="mt-3">
                The Company will consider appointing a Data Protection Officer (DPO) as the organisation grows and processing activities develop, in accordance with Article 37 UK GDPR.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="complaints">
              <h2 className="text-2xl font-semibold text-foreground mb-4">14. Complaints and Supervisory Authority</h2>
              <p>If you believe the Company has not handled your personal data appropriately, you have the right to lodge a complaint with the Information Commissioner’s Office (ICO):</p>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm border border-border">
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2 font-bold bg-muted">Website</td>
                      <td className="px-4 py-2">
                        <a href="https://www.ico.org.uk" className="text-primary hover:underline">www.ico.org.uk</a>
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="px-4 py-2 font-bold bg-muted">Telephone</td>
                      <td className="px-4 py-2">0303 123 1113</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-bold bg-muted">Post</td>
                      <td className="px-4 py-2">Information Commissioner’s Office, Wycliffe House, Water Lane, Wilmslow, SK9 5AF</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                We encourage you to contact us first at <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a> to resolve any concerns directly.
              </p>
            </section>

            <hr className="my-8 border-border" />

            <section id="review">
              <h2 className="text-2xl font-semibold text-foreground mb-4">15. Policy Review</h2>
              <p>
                This GDPR Policy will be reviewed annually or following any significant change in processing activities, applicable legislation, or organisational structure. The review date and version number are recorded on the cover of this document.
              </p>
              <p className="mt-3">
                For all data protection enquiries, please contact Source Code Control Limited at: <a href="mailto:open@sourcecodecontrol.co" className="text-primary hover:underline">open@sourcecodecontrol.co</a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}